import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, Card, CardContent, Chip, IconButton, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Button } from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { AlarmList } from '../../components/Alarms/AlarmList';
import { AlarmHistory } from './AlarmHistory';
import { useSiteContext } from '../../hooks/useSiteContext';
import { SCADAAlarm } from '../../types';
import { scadaAlarmsAPI } from '../../services/alarmService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`alarm-tabpanel-${index}`}
      aria-labelledby={`alarm-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const AlarmDashboard: React.FC = () => {
  const { currentSite } = useSiteContext();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [statistics, setStatistics] = useState<any>(null);
  
  // Selected alarm for details
  const [selectedAlarm, setSelectedAlarm] = useState<SCADAAlarm | null>(null);
  const [showAlarmDetails, setShowAlarmDetails] = useState(false);

  const fetchDashboardData = async () => {
    if (!currentSite?.siteId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [dashboard, stats] = await Promise.all([
        scadaAlarmsAPI.getDashboard({ site_id: currentSite.siteId }),
        scadaAlarmsAPI.getStatistics(30) // Get statistics for last 30 days
      ]);
      
      setDashboardData(dashboard);
      setStatistics(stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // Admin-only config removed from frontend
  }, [currentSite?.siteId]);

  // Auto-refresh dashboard data
  useEffect(() => {
    const interval = setInterval(fetchDashboardData, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, [currentSite?.siteId]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'LOW': return 'success';
      case 'MEDIUM': return 'warning';
      case 'HIGH': return 'error';
      case 'CRITICAL': return 'error';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'LOW': return <InfoIcon />;
      case 'MEDIUM': return <WarningIcon />;
      case 'HIGH': return <ErrorIcon />;
      case 'CRITICAL': return <ErrorIcon />;
      default: return <InfoIcon />;
    }
  };

  if (!currentSite) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a site to view alarm information.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Alarm Management - {currentSite.siteName}
        </Typography>
        <IconButton onClick={fetchDashboardData} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {dashboardData && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Total Alarms
                    </Typography>
                    <Typography variant="h4">
                      {dashboardData.total_alarms}
                    </Typography>
                  </Box>
                  <WarningIcon color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Active Alarms
                    </Typography>
                    <Typography variant="h4" color="error">
                      {dashboardData.active_alarms}
                    </Typography>
                  </Box>
                  <ErrorIcon color="error" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Acknowledged
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {dashboardData.acknowledged_alarms}
                    </Typography>
                  </Box>
                  <CheckCircleIcon color="warning" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom>
                      Avg Response Time
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {Math.round(((statistics?.average_response_time ?? 0) / 60))}m
                    </Typography>
                  </Box>
                  <ScheduleIcon color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Priority Breakdown */}
      {dashboardData && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {Object.entries(dashboardData.alarms_by_priority || {}).map(([priority, count]) => (
            <Grid item xs={6} sm={3} key={priority}>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                  {getPriorityIcon(priority)}
                </Box>
                <Typography variant="h6" color={`${getPriorityColor(priority)}.main`}>
                  {count as number}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {priority}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Active Alarms" />
          <Tab label="History" />
          <Tab label="Statistics" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <AlarmList
            siteId={currentSite.siteId}
            onlyActive
            onAlarmSelect={(alarm) => {
              setSelectedAlarm(alarm);
              setShowAlarmDetails(true);
            }}
            refreshInterval={30000}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <AlarmHistory />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" sx={{ mb: 2 }}>Alarm Statistics</Typography>
          
          {dashboardData && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Alarm Types</Typography>
                  {Object.entries(dashboardData.alarms_by_type || {}).map(([type, count]) => (
                    <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{type}</Typography>
                      <Typography variant="body2" fontWeight="bold">{count as number}</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Performance Metrics</Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Average Response Time</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {Math.round(((statistics?.average_response_time ?? 0) / 60))} minutes
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Total Alarms</Typography>
                    <Typography variant="body2" fontWeight="bold">{dashboardData.total_alarms}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Active Alarms</Typography>
                    <Typography variant="body2" fontWeight="bold" color="error.main">
                      {dashboardData.active_alarms}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </TabPanel>
      </Paper>

      {/* Admin-only dialogs removed */}

      {/* Alarm Details Dialog */}
      <Dialog open={showAlarmDetails} onClose={() => setShowAlarmDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>Alarm Details</DialogTitle>
        <DialogContent>
          {selectedAlarm && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Tag Name</Typography>
                <Typography variant="body1">{selectedAlarm.tag_name}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Alarm Type</Typography>
                <Typography variant="body1">{selectedAlarm.alarm_type}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Priority</Typography>
                <Chip
                  label={selectedAlarm.priority}
                  color={getPriorityColor(selectedAlarm.priority) as any}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">State</Typography>
                <Chip
                  label={selectedAlarm.state}
                  color={selectedAlarm.state === 'ACTIVE' ? 'error' : 'default'}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Alarm Value</Typography>
                <Typography variant="body1">{selectedAlarm.alarm_value}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Setpoint</Typography>
                <Typography variant="body1">{selectedAlarm.setpoint}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Triggered At</Typography>
                <Typography variant="body1">
                  {new Date(selectedAlarm.triggered_at).toLocaleString()}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">Duration</Typography>
                <Typography variant="body1">{selectedAlarm.duration}</Typography>
              </Grid>
              
              {selectedAlarm.acknowledged_by_name && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="textSecondary">Acknowledged By</Typography>
                  <Typography variant="body1">{selectedAlarm.acknowledged_by_name}</Typography>
                </Grid>
              )}
              
              {selectedAlarm.acknowledgment_comment && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">Acknowledgment Comment</Typography>
                  <Typography variant="body1">{selectedAlarm.acknowledgment_comment}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAlarmDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 